import React, { useState, useEffect } from "react";
import i18n from "../lib/i18n";
import useBooleanSwitch from "../hooks/useBooleanSwitch";
import useTimestamp from "../hooks/useTimestamp";
import UIBox from "./UIBox";
import DepositionChart from "./DepositionChart";
import DepositionSettings from "./DepositionSettings";
import KPIBox from "./KPIBox";

import MaintenancePlan from "../lib/MaintenancePlan";
import { qs } from "../lib/helpers";

const DepositionAnalysis = (props) => {
  const [deposition, setDeposition] = useState({});
  const [settingsUpdated, touchSettingsUpdated] = useTimestamp();
  const { plan, includeVAT } = props;
  const facilityId = plan?.attributes?.facility_id;

  const onChange = async (attributes) => {
    await MaintenancePlan.update(plan.id, attributes);
    touchSettingsUpdated();
  };

  useEffect(() => {
    (async () => {
      const response = await fetch(
        `/facilities/${facilityId}/plans/${plan.id}/actions/deposition_analysis?${qs({ include_vat: includeVAT })}`
      );
      const data = await response.json();
      setDeposition(data);
    })();
  }, [plan.id, facilityId, settingsUpdated, includeVAT]);

  useBooleanSwitch(props.isRecalculating, {
    offCallback: touchSettingsUpdated,
  });

  return (
    <>
      <UIBox>
        <h2>{i18n.t("js.components.deposition_analysis.title")}</h2>
        <p
          dangerouslySetInnerHTML={{
            __html: i18n.t("js.components.deposition_analysis.info"),
          }}
        />

        <div style={{ height: "400px", width: "100%" }}>
          <DepositionChart data={getDepositionChartData(deposition)} />
        </div>
      </UIBox>

      <div className="row">
        <div className="col-md-6">
          <KPIBox
            title={i18n.t(
              "js.components.deposition_analysis.recommended_deposition"
            )}
          >
            <div className="c-stats-value">
              <div className="c-stats-value__content">
                <span className="c-stats-value__value">
                  {deposition.alt_deposition_per_residential_area
                    ? deposition.alt_deposition_per_residential_area
                    : i18n.t("js.components.deposition_analysis.not_defined")}
                </span>
                {deposition.alt_deposition_per_residential_area ? (
                  <span className="c-stats-value__unit">
                    {" "}
                    {i18n.t("js.helpers.currency")} / m&sup2;
                  </span>
                ) : null}
              </div>
            </div>
          </KPIBox>
        </div>
        <div className="col-md-6">
          <KPIBox
            title={i18n.t(
              "js.components.deposition_analysis.current_deposition"
            )}
          >
            <div className="c-stats-value">
              <div className="c-stats-value__content">
                <span className="c-stats-value__value">
                  {deposition.deposition_per_residential_area
                    ? deposition.deposition_per_residential_area
                    : i18n.t("js.components.deposition_analysis.not_defined")}
                </span>
                {deposition.deposition_per_residential_area ? (
                  <span className="c-stats-value__unit">
                    {" "}
                    {i18n.t("js.helpers.currency")} / m&sup2;
                  </span>
                ) : null}
              </div>
            </div>
          </KPIBox>
        </div>
      </div>

      <hr />

      <DepositionSettings {...props} onChange={onChange} />
    </>
  );
};

const getDepositionChartData = (data) => {
  return {
    years: data.years,
    series: [
      {
        name: i18n.t("js.components.deposition_analysis.balance_recommended"),
        data: data.alt_sums,
      },
      {
        name: i18n.t("js.components.deposition_analysis.balance_current"),
        data: data.sums,
      },
    ],
  };
};

export default DepositionAnalysis;
